/* eslint-disable promise/prefer-await-to-then */
import 'regenerator-runtime/runtime';

import getConfig from 'next/config';
import Router from 'next/router';
import { appWithTranslation } from 'next-i18next';
import { lazy as reactLazy, Suspense, useEffect, useState } from 'react';
import { useStore } from 'react-redux';
// Prevent fontawesome from adding its CSS since we did it manually above:
import { config } from '@fortawesome/fontawesome-svg-core';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { setAppConfig } from '@store/config/config.slice';
import { setNativeAppConfig } from '@store/nativeAppConfig/nativeAppConfig.slice';
import { wrapper } from '@store/store';
import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister';
import {
  dehydrate,
  Hydrate,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { persistQueryClient } from '@tanstack/react-query-persist-client';
import { getCookie, setCookie } from 'cookies-next';
import deepmerge from 'deepmerge';
import isEmpty from 'lodash/isEmpty';
import NProgress from 'nprogress';
import TailwindGlobalStyles from '@components/TailwindGlobalStyles';
import TIME from '@constants/time';
import { NavigationProvider } from '@contexts/Navigation.context';
import PostHogWrapper from '@features/PostHog/PostHogWrapper.js';
import { getLanguages } from '@hooks/useLanguages';
import { getOrderForm } from '@hooks/useOrderForm';
import { getTranslations } from '@hooks/useTranslations';
import getAppConfig from '@utils/getAppConfig';
import setUpInterceptor from '@utils/setUpInterceptor';
import App from '@views/App/App';
import { getHostAxiosConfig, setServerHostConfig } from '@utils/hostConfig';
import { fetchWithServerCache } from 'utils/serverCache.js';

import 'swiper/css';

import nextI18NextConfig from '../next-i18next.config.js';

import 'nprogress/nprogress.css';
import 'react-toastify/dist/ReactToastify.css';
import '@assets/reactDayPicker.css';
// The following import prevents a Font Awesome icon server-side rendering bug,
// where the icons flash from a very large icon down to a properly sized one:
import '@fortawesome/fontawesome-svg-core/styles.css';

if (process.env.NODE_ENV !== 'development') {
  // eslint-disable-next-line @typescript-eslint/no-require-imports -- FIXME
  require('source-map-support/register');
}

config.autoAddCss = false;

const queryClientOptions = {
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
      // eslint-disable-next-line @stylistic/line-comment-position -- FIXME
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
};

const getServerCacheKey = (type, locale, host) =>
  `${type}:${locale || 'default'}:${host || 'unknown'}`;
const createBootstrapQueryClient = async (queryEntries = []) => {
  const queryClient = new QueryClient(queryClientOptions);

  queryClient.setQueryData(['stripeKeys'], {
    secret: null,
    key: null,
  });
  queryClient.setQueryData(['paymentCards'], []);
  queryClient.setQueryData(['order'], {});

  await Promise.all(
    queryEntries.map(({ queryKey, data }) =>
      queryClient.prefetchQuery({
        queryKey,
        queryFn: () => data,
      })
    )
  );

  return dehydrate(queryClient);
};

const NOINDEX_ROUTE_PREFIXES = ['/new-order'];

const buildBasePageProps = response => ({
  configError: false,
  bodyTags: response.branding.bodyTags,
  brandName: response.branding.name,
  currencyCode: response.multinational?.defaultRegion?.currencyCode,
  gtmModule: response.modules?.GoogleTagManager || {},
});

// page-props size logger removed (too noisy for current debugging)

const resolveLocalePageProps = ({
  response,
  responseLanguages,
  routerLocale,
  cookieNextLocale,
  defaultLocale,
  ctx,
  router,
  req,
  res,
  basePageProps,
  include404Check = false,
}) => {
  if (include404Check && ctx?.res?.statusCode === 404) {
    return {};
  }

  const { clientAllowedToChangeLanguage } = response.multinational;
  const normalizedRouterLocale = (routerLocale || '').toLowerCase();

  let cookieLangExistInLangArray = false;
  if (cookieNextLocale) {
    cookieLangExistInLangArray = responseLanguages.some(
      ({ isoCode, enabledForClient }) =>
        enabledForClient &&
        isoCode.toLowerCase() === cookieNextLocale.toLowerCase()
    );
  }

  const routerLangExistInLangArray = responseLanguages.some(
    ({ isoCode, enabledForClient }) =>
      enabledForClient && isoCode.toLowerCase() === normalizedRouterLocale
  );

  if (
    !clientAllowedToChangeLanguage ||
    (clientAllowedToChangeLanguage &&
      !routerLangExistInLangArray &&
      !cookieLangExistInLangArray)
  ) {
    setCookie('NEXT_LOCALE', defaultLocale, {
      req,
      res,
      maxAge: TIME.MONTH_IN_SECONDS,
    });

    return { ...basePageProps, locale: defaultLocale };
  }

  if (
    cookieNextLocale &&
    routerLocale !== cookieNextLocale &&
    routerLocale === defaultLocale
  ) {
    ctx.res.writeHead(302, {
      Location: `/${cookieNextLocale}${router.asPath}`,
    });
    ctx.res.end();

    return {};
  }

  setCookie('NEXT_LOCALE', routerLocale, {
    req,
    res,
    maxAge: TIME.MONTH_IN_SECONDS,
  });

  return { ...basePageProps, locale: routerLocale };
};

let browserQueryClient;
const getBrowserQueryClient = () => {
  if (!browserQueryClient) {
    browserQueryClient = new QueryClient(queryClientOptions);
  }

  return browserQueryClient;
};

if (typeof window !== 'undefined') {
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: AsyncStorage,
  });

  persistQueryClient({
    queryClient: getBrowserQueryClient(),
    persister: asyncStoragePersister,
  });
}

let isMountedInterceptor = false;

(function setUpServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  const isWorkboxAvailable = 'workbox' in window;

  if (isWorkboxAvailable) {
    window.workbox.register();
  }

  const currentServiceWorker = navigator.serviceWorker.controller?.scriptURL;

  if (!currentServiceWorker || currentServiceWorker?.includes('/sw.js')) {
    return;
  }

  navigator.serviceWorker
    .register('/service-worker.js')

    .then(registration => {
      registration.update();

      registration.onupdatefound = () => {
        const installingWorker = registration.installing;

        installingWorker.onstatechange = () => {
          if (
            installingWorker.state === 'installed' &&
            navigator.serviceWorker.controller
          ) {
            installingWorker.postMessage({ action: 'skipWaiting' });
          }
        };
      };
    })
    // eslint-disable-next-line promise/prefer-await-to-callbacks -- FIXME
    .catch(error => {
      // eslint-disable-next-line no-console -- FIXME
      console.error('Service worker registration failed:', error);
    });

  navigator.serviceWorker.addEventListener('message', e => {
    if (e.data?.action === 'impersonatedForeignServiceWorkerActivation') {
      if (isWorkboxAvailable) {
        window.workbox.register().then(() => {
          window.location.reload();
        });
      } else {
        window.location.reload();
      }
    }
  });
})();

const ReactQueryDevtoolsProduction = reactLazy(() =>
  import('@tanstack/react-query-devtools/build/lib/index.prod.js').then(d => ({
    default: d.ReactQueryDevtools,
  }))
);

const MyApp = ({ Component, pageProps, router }) => {
  const store = useStore(state => state);
  const [
    showReactQueryDevtoolsProduction,
    setShowReactQueryDevtoolsProduction,
  ] = useState(false);
  const [queryClient] = useState(() =>
    typeof window === 'undefined'
      ? new QueryClient(queryClientOptions)
      : getBrowserQueryClient()
  );

  if (typeof window !== 'undefined' && !isMountedInterceptor) {
    setUpInterceptor(store);
    isMountedInterceptor = true;
  }

  useEffect(() => {
    // Capturing query settings for native app
    store.dispatch(
      setNativeAppConfig({
        isNativeApp: router?.query?.isNativeApp === '1',
        disableTracking: router?.query?.disableTracking === '1',
        useNativeBridge: router?.query?.useNativeBridge === '1',
        isNativeBridgeDebugEnabled:
          router?.query?.isNativeBridgeDebugEnabled === '1',
        disableThirdPartyLoginMethods:
          router?.query?.disableThirdPartyLoginMethods === '1',
      })
    );

    store.dispatch(
      setAppConfig({
        brandName: pageProps.brandName,
        currencyCode: pageProps.currencyCode,
      })
    );

    NProgress.configure({
      showSpinner: false,
    });

    Router.onRouteChangeStart = () => {
      NProgress.start();
    };

    Router.onRouteChangeComplete = () => {
      NProgress.done();
    };

    Router.onRouteChangeError = () => {
      NProgress.done();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIXME
  }, []);

  useEffect(() => {
    const $body = document.body;
    $body.classList.add(pageProps.bodyClassName);

    const localeScript = document.createElement('script');
    localeScript.innerHTML = `window.__localeId__ = "${pageProps.locale}"`;
    $body.appendChild(localeScript);

    return () => {
      $body.classList.remove(pageProps.bodyClassName);
    };
  }, [pageProps.bodyClassName, pageProps.locale]);

  useEffect(() => {
    window.toggleReactQueryDevtools = () =>
      setShowReactQueryDevtoolsProduction(prev => !prev);
  }, []);

  const {
    appDehydratedState = {},
    dehydratedState = {},
    ...restPageProps
  } = pageProps;

  const mergedDehydratedState = isEmpty(dehydratedState)
    ? appDehydratedState
    : deepmerge(appDehydratedState, dehydratedState);

  return (
    <NavigationProvider>
      <QueryClientProvider client={queryClient}>
        <TailwindGlobalStyles />
        <Hydrate state={mergedDehydratedState}>
          <PostHogWrapper>
            <App
              Component={Component}
              pageProps={restPageProps}
              store={store}
            />
          </PostHogWrapper>
          <ReactQueryDevtools initialIsOpen={false} />
          {showReactQueryDevtoolsProduction && (
            <Suspense fallback={null}>
              <ReactQueryDevtoolsProduction />
            </Suspense>
          )}
        </Hydrate>
      </QueryClientProvider>
    </NavigationProvider>
  );
};

MyApp.getInitialProps = async context => {
  const { publicRuntimeConfig } = getConfig();
  const { ctx, router } = context;
  const { req, res } = ctx;
  const isNextRedirect = req ? req.url.includes('_next') : true;

  const xForwardedFor = req?.headers?.['x-forwarded-for'];
  let ip = req?.ip ?? req?.headers?.['x-real-ip'];
  if (!ip && xForwardedFor) {
    ip = xForwardedFor.split(',').at(0) ?? req?.connection?.remoteAddress;
  }
  const requestHeaders = {};
  if (xForwardedFor) {
    requestHeaders['x-forwarded-for'] = xForwardedFor;
  }
  if (ip) {
    requestHeaders['x-real-ip'] = ip;
  }

  if (isNextRedirect || router.asPath.includes('/images')) {
    return { pageProps: { configError: false } };
  }

  const { host } = req.headers;
  const hostConfig = getHostAxiosConfig(host);

  setServerHostConfig(hostConfig);

  if (isEmpty(hostConfig.baseURL)) {
    return { pageProps: { configError: false } };
  }

  const routerLocale = router.locale;
  const cookieNextLocale = getCookie('NEXT_LOCALE', { req, res });
  const { defaultLocale } = publicRuntimeConfig;
  const isLightSSR = router.asPath?.startsWith('/new-order/create');

  try {
    const resolvedLocale = routerLocale || cookieNextLocale || defaultLocale;
    const configRequestOptions = {
      xLocale: resolvedLocale,
      headers: {
        ...requestHeaders,
        'X-LOCALE': resolvedLocale,
      },
    };
    const cacheKeyLocale = resolvedLocale?.toLowerCase();

    const fetchBootstrap = (cacheKey, { includeOrderForm }) =>
      fetchWithServerCache(cacheKey, async () => {
        const orderFormPromise = includeOrderForm
          ? fetchWithServerCache(
              getServerCacheKey('orderForm', cacheKeyLocale, host),
              () => getOrderForm(configRequestOptions)
            )
          : Promise.resolve(null);

        const [
          response,
          responseLanguages,
          responseOrderForm,
          responseTranslations,
        ] = await Promise.all([
          fetchWithServerCache(
            getServerCacheKey('appConfig', cacheKeyLocale, host),
            () => getAppConfig(configRequestOptions)
          ),
          fetchWithServerCache(
            getServerCacheKey('languages', cacheKeyLocale, host),
            () => getLanguages(configRequestOptions)
          ),
          orderFormPromise,
          fetchWithServerCache(
            getServerCacheKey('apiTranslations', cacheKeyLocale, host),
            () => getTranslations(configRequestOptions)
          ),
        ]);

        const queryEntries = [
          { queryKey: ['appConfig'], data: response },
          { queryKey: ['languages'], data: responseLanguages },
          {
            queryKey: ['apiTranslations', resolvedLocale],
            data: responseTranslations,
          },
        ];
        if (includeOrderForm) {
          queryEntries.push({
            queryKey: ['orderForm'],
            data: responseOrderForm,
          });
        }

        const appDehydratedState = await createBootstrapQueryClient(
          queryEntries
        );

        return { appDehydratedState, response, responseLanguages };
      });

    const bootstrap = await fetchBootstrap(
      getServerCacheKey(
        isLightSSR ? 'bootstrap-light' : 'bootstrap',
        cacheKeyLocale,
        host
      ),
      { includeOrderForm: !isLightSSR }
    );

    const { response, responseLanguages, appDehydratedState } = bootstrap;

    const isIndexingEnabled = response?.modules?.Seo?.indexing ?? false;
    const isNoindexRoute = NOINDEX_ROUTE_PREFIXES.some(prefix =>
      router.asPath?.startsWith(prefix)
    );
    if (res && !res.headersSent && !isIndexingEnabled && isNoindexRoute) {
      res.setHeader('X-Robots-Tag', 'noindex, nofollow, nosnippet');
    }

    const basePageProps = {
      ...buildBasePageProps(response),
      appDehydratedState,
      apiBaseUrl: hostConfig.baseURL,
      xCKey: hostConfig.xCKey,
    };

    const resolvedPageProps = resolveLocalePageProps({
      response,
      responseLanguages,
      routerLocale,
      cookieNextLocale,
      defaultLocale,
      ctx,
      router,
      req,
      res,
      basePageProps,
      include404Check: !isLightSSR,
    });

    return {
      pageProps: resolvedPageProps,
    };
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- FIXME
  } catch (error) {
    return {
      pageProps: {
        configError: !['/404', '/500'].includes(router.route),
      },
    };
  }
};

export default wrapper.withRedux(appWithTranslation(MyApp, nextI18NextConfig));
